<template>
  <f7-navbar
    class="at-navbar"
    :sliding="false"
  >
    <f7-nav-left back-click>
      <!-- Back -->
      <f7-link
        v-if="backBtnEnable"
        icon="icon-custom-back"
        @click="goBack"
      />

      <!-- Menu -->
      <f7-link
        v-if="menuBtnEnable"
        icon="icon-menu"
        panel-open="left"
      />

      <div
        class="title color-white"
      >
        {{ navTitle }}
      </div>
    </f7-nav-left>

    <f7-nav-right class="nav-right">
      <slot name="right" />

      <PCNotificationAlertERP
        v-if="notificationsErpBtnEnable"
      />

      <!-- Refresh -->
      <f7-link
        v-if="refreshBtnEnable"
        icon="icon-reload"
        @click="emitRefreshBtn"
      />

      <!-- Notifications -->
      <f7-link
        v-if="notificationsBtnEnable"
        @click="notificationsClicked"
      >
        <f7-icon icon="icon-notifications">
          <f7-badge
            v-if="messages.length > 0"
          >
            {{ messages.length }}
          </f7-badge>
        </f7-icon>
      </f7-link>

      <!-- Edit -->
      <f7-link
        v-if="editBtnEnable"
        icon="icon-edit"
        @click="navigateToEdit"
      />

      <!-- Add -->
      <f7-link
        v-if="addBtnEnable"
        @click="emitAddBtn"
      >
        <f7-icon icon="icon-addNew" />
      </f7-link>

      <!-- Delete -->
      <f7-link
        v-if="deleteBtnEnable"
        @click="emitDeleteBtn"
      >
        <f7-icon icon="icon-delete" />
      </f7-link>

      <!-- QR -->
      <f7-link
        v-if="qrBtnEnable"
        @click="emitQrBtn"
      >
        <f7-icon icon="icon-qr" />
      </f7-link>

      <!-- Close Popup -->
      <f7-link
        v-if="popupCloseBtnEnable"
        popup-close
      >
        <f7-icon icon="icon-close" />
      </f7-link>

      <slot name="btn-right" />
    </f7-nav-right>

    <div v-if="searchBarEnable">
      <f7-subnavbar :inner="false">
        <f7-searchbar
          search-container=".virtual-list"
          search-item="li"
          search-in=".item-title"
          :placeholder="t('common.search')"
        />
      </f7-subnavbar>
    </div>
  </f7-navbar>
</template>

<script setup>
// @ts-check
import { ref } from 'vue';
import { f7 } from 'framework7-vue';

import PCNotificationAlertERP from '@/components/common/PCNotificationAlertERP.vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  searchBarEnable: { type: Boolean, default: false },
  deleteBtnEnable: { type: Boolean, default: false },
  addBtnEnable: { type: Boolean, default: false },
  backBtnEnable: { type: Boolean, default: false },
  qrBtnEnable: { type: Boolean, default: false },
  menuBtnEnable: { type: Boolean, default: false },
  refreshBtnEnable: { type: Boolean, default: false },
  notificationsBtnEnable: { type: Boolean, default: false },
  notificationsErpBtnEnable: { type: Boolean, default: false },
  editBtnEnable: { type: Boolean, default: false },
  popupCloseBtnEnable: { type: Boolean, default: false },
  navTitle: { type: String, default: '' },
  backCallback: { type: Function, default: undefined },
});

const emit = defineEmits([
  'add-btn',
  'qr-btn',
  'delete-btn',
  'refresh-btn',
]);

const { t } = useI18n();
const messages = ref([]);

function goBack() {
  if (props.backCallback) {
    props.backCallback();
  } else {
    f7.views.main.router.back();
  }
}

function navigateToEdit() {
  f7.views.main.router.navigate('/editalert/');
}

function notificationsClicked() {
  f7.views.main.router.navigate('/massivemesages/');
}

function emitAddBtn() {
  emit('add-btn');
}

function emitQrBtn() {
  emit('qr-btn');
}

function emitDeleteBtn() {
  emit('delete-btn');
}

function emitRefreshBtn() {
  emit('refresh-btn');
}
</script>

<style lang="scss">
.at-navbar,
.content-popup,
.navbar {
  .icon {
    width: 38px;
    height: 38px;
  }

  .left {
    // This fixes the overflow of the title
    overflow: hidden;
    flex-shrink: inherit;
  }

  .title {
    font-size: 15px;
    color: var(--f7-color-white);
  }

  .icon-custom-back {
    background-image: url("@/static/icons/ic_back.svg");
    width: 24px;
    height: 24px;
  }

  .icon-reload {
    background-image: url("@/static/icons/ic_refresh.svg");
  }

  .icon-scan-nfc {
    background-image: url("@/static/icons/ic_nfc.svg");
  }

  .icon-add {
    background-image: url("@/static/icons/ic_new2.svg");
  }

  .icon-print {
    background-image: url("@/static/icons/ic_print.svg");
  }

  .icon-addNew {
    background-image: url("@/static/icons/ic_add_new.svg");
  }

  .icon-qr {
    background-image: url("@/static/icons/ic_qr.svg");
  }

  .icon-notifications {
    background-image: url("@/static/icons/ic_notifications.svg");
  }

  .icon-edit {
    background-image: url("@/static/img/ic_button_edit.svg");
  }

  .icon-delete {
    background-image: url("@/static/icons/ic_delete.svg");
  }

  .icon-deleteGris {
    background-image: url("@/static/icons/ic_deleteGris.svg");
  }

  .icon_duplicate {
    background-image: url("@/static/icons/ic_duplicateGris.svg");
  }

  .icon-options_dark {
    background-image: url("@/static/icons/ic_options_dark.svg");
  }

  .icon-task_list {
    background-image: url("@/static/icons/ic_task-list.svg");
  }

  .icon-reload-blue {
    background-image: url("@/static/icons/ic_refresh_blue.svg");
    border-radius: 15px;
  }

  .icon-arrow_down_to_line {
    background-image: url("@/static/icons/ic_download.svg");
  }

  .icon-menu {
    background-image: url("@/static/icons/ic_menu.svg");
    width: 24px;
    height: 24px;
  }

  .icon-back {
    background-image: url("@/static/icons/ic_back.svg");
    width: 24px;
    height: 24px;

    &::after {
      display: none;
    }
  }

  .icon-close {
    background-image: url("@/static/icons/ic_close.svg");
    width: 24px;
    height: 24px;
  }

  .refresh-preloader {
    padding: 0 16px;
    min-width: 32px;
  }
}

.smart-select-page {
  .navbar {
    a.link {
      padding-left: 0 !important;
      min-width: 0;
    }
  }
}
</style>
